import { useState } from 'react'
import { Link } from 'react-router'
import { Icon } from '../components/Icon'
import { formatDuration } from '../lib/format'
import { RATES, useAudio } from './AudioProvider'
import { Scrubber } from './Scrubber'

/**
 * Docked at the bottom of every page while something is loaded. Because it
 * lives above the router outlet, playback survives navigation — you can start a
 * theory, keep browsing the archive, and it follows you.
 */
export function MiniPlayer() {
  const audio = useAudio()
  const [rateOpen, setRateOpen] = useState(false)

  if (!audio.track) return null
  const { track } = audio

  const cycleRate = () => {
    const index = RATES.indexOf(audio.rate as (typeof RATES)[number])
    audio.setRate(RATES[(index + 1) % RATES.length] ?? 1)
  }

  return (
    <div className="mini-player" role="region" aria-label="Audio player">
      <div className="mini-player-scrub">
        <Scrubber
          compact
          currentTime={audio.currentTime}
          duration={audio.duration}
          buffered={audio.buffered}
          onSeek={audio.seek}
          label="Seek within the recording"
        />
      </div>

      <div className="mini-player-body page">
        <Link to={`/theory/${track.slug}`} className="mini-player-meta">
          {track.coverImage ? (
            <img
              src={track.coverImage}
              alt=""
              className="mini-player-art"
              width={48}
              height={48}
              loading="lazy"
            />
          ) : (
            <span className="mini-player-art mini-player-art-blank" aria-hidden="true">
              <Icon name="audio" size={18} />
            </span>
          )}
          <span className="mini-player-text">
            <span className="mini-player-title">{track.title}</span>
            <span className="mini-player-time mono">
              {formatDuration(audio.currentTime)}
              <span className="mini-player-sep"> / </span>
              {formatDuration(audio.duration || track.audio?.seconds)}
              {audio.error && <span className="mini-player-error"> · {audio.error}</span>}
            </span>
          </span>
        </Link>

        <div className="mini-player-controls">
          <button
            type="button"
            className="btn btn-subtle btn-icon mini-player-skip"
            onClick={() => audio.skip(-15)}
            aria-label="Back 15 seconds"
            title="Back 15 seconds (Shift + ←)"
          >
            <Icon name="back15" />
          </button>

          <button
            type="button"
            className="mini-player-play"
            onClick={audio.toggle}
            aria-label={audio.playing ? 'Pause' : 'Play'}
            title={audio.playing ? 'Pause (Space)' : 'Play (Space)'}
          >
            {audio.loading ? (
              <span className="spinner" />
            ) : (
              <Icon name={audio.playing ? 'pause' : 'play'} />
            )}
          </button>

          <button
            type="button"
            className="btn btn-subtle btn-icon mini-player-skip"
            onClick={() => audio.skip(30)}
            aria-label="Forward 30 seconds"
            title="Forward 30 seconds (Shift + →)"
          >
            <Icon name="forward30" />
          </button>

          {audio.hasNext && (
            <button
              type="button"
              className="btn btn-subtle btn-icon mini-player-extra"
              onClick={audio.playNext}
              aria-label="Next theory"
              title="Next theory"
            >
              <Icon name="next" />
            </button>
          )}
        </div>

        <div className="mini-player-aux">
          <div className="rate-control">
            <button
              type="button"
              className="btn btn-subtle btn-sm rate-button"
              onClick={cycleRate}
              onContextMenu={(event) => {
                event.preventDefault()
                setRateOpen((open) => !open)
              }}
              aria-label={`Playback speed ${audio.rate}x. Click to change.`}
              title="Playback speed"
            >
              {audio.rate}×
            </button>
            {rateOpen && (
              <ul className="rate-menu" role="menu">
                {RATES.map((rate) => (
                  <li key={rate}>
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={audio.rate === rate}
                      className={audio.rate === rate ? 'is-active' : ''}
                      onClick={() => {
                        audio.setRate(rate)
                        setRateOpen(false)
                      }}
                    >
                      {rate}×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="volume-control mini-player-extra">
            <button
              type="button"
              className="btn btn-subtle btn-icon"
              onClick={audio.toggleMute}
              aria-label={audio.muted ? 'Unmute' : 'Mute'}
            >
              <Icon name={audio.muted || audio.volume === 0 ? 'muted' : 'volume'} />
            </button>
            <input
              type="range"
              className="volume-slider"
              min={0}
              max={1}
              step={0.05}
              value={audio.muted ? 0 : audio.volume}
              onChange={(event) => audio.setVolume(Number(event.target.value))}
              aria-label="Volume"
            />
          </div>

          <button
            type="button"
            className="btn btn-subtle btn-icon"
            onClick={audio.stop}
            aria-label="Close player"
            title="Close player"
          >
            <Icon name="close" />
          </button>
        </div>
      </div>
    </div>
  )
}
